import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { blogPosts } from "../../data/blogPosts";

const ASSET_BASE = process.env.PUBLIC_URL || "";

function Blogs() {
  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const visiblePosts = blogPosts.slice(0, visibleCount);
  const hasMore = visibleCount < blogPosts.length;

  return (
    <Box component="main">
      <Box
        component="section"
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: "50vh", md: "75vh" },
          overflow: "hidden",
        }}
      >
        <Box
          component="video"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            pointerEvents: "none",
          }}
        >
          <source
            src={`${ASSET_BASE}/assets/blogsvideo.mp4`}
            type="video/mp4"
          />
        </Box>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            px: { xs: 3, md: "64px" },
            color: "common.white",
            textShadow: "0 2px 6px rgba(0,0,0,0.35)",
            pointerEvents: "none",
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2.25rem", sm: "2.75rem", md: "3.5rem" },
              fontWeight: 700,
              lineHeight: 1.2,
              mb: 2,
              animation: "heroFadeUp 0.9s ease both",
              "@keyframes heroFadeUp": {
                from: { opacity: 0, transform: "translateY(36px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            Explore fresh
            <br />
            perspectives and
            <br />
            inspiring stories
            <br />
            from our leaders
          </Typography>
          <Typography
            sx={{ fontSize: { xs: "1.1rem", md: "1.4rem" }, fontWeight: 200 }}
          >
            Driven by Values, Focused on Purpose
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          px: { xs: 3, md: "64px" },
          mt: { xs: 1, md: 3 },
          mb: { xs: 4, md: 8 },
        }}
      >
        <Typography
          variant="h2"
          sx={{
            textAlign: "left",
          }}
        >
          Blogs
        </Typography>
      </Box>
      <Box
        component="section"
        sx={{
          px: { xs: 3, md: 8 },
          pt: { xs: 4, md: 6 },
          pb: { xs: 5, md: 9 },
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: { xs: 3, md: 6 },
            width: "100%",
            mx: "auto",
          }}
        >
          {visiblePosts.map((post) => (
            <Box
              component="article"
              key={post.id}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <Link
                to={post.href}
                style={{ textDecoration: "none", lineHeight: 0 }}
              >
                <Box
                  sx={{
                    width: "100%",
                    borderRadius: "24px 24px 0px 24px",
                    overflow: "hidden",
                    lineHeight: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={post.img.src}
                    alt={post.img.alt}
                    sx={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              </Link>

              <Typography
                variant="caption"
                sx={{ color: "grey.600", mt: 2, mb: 1, fontSize: "0.75rem" }}
              >
                {post.date}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontFamily:
                    "'Anko Bold', Helvetica, Arial, Lucida, sans-serif !important",
                  fontWeight: 600,
                  mb: 1.5,
                  fontSize: "1rem",
                }}
              >
                <Box
                  component={Link}
                  to={post.href}
                  sx={{ textDecoration: "none", color: "common.black" }}
                ></Box>
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.primary",
                  lineHeight: 1.6,
                  mb: 2,
                  fontSize: "13px",
                  fontWeight: 400,
                  letterSpacing: "0.05em",
                  fontFamily:
                    "'Anko Light', Helvetica, Arial, Lucida, sans-serif !important",
                }}
              >
                {post.excerpt}
              </Typography>
              <Box
                component={Link}
                to={post.href}
                sx={{
                  color: "primary.main",
                  fontWeight: 500,
                  textDecoration: "none",
                  fontSize: "0.85rem",
                }}
              >
                READ MORE &rarr;
              </Box>
            </Box>
          ))}
        </Box>

        {hasMore && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <Button
              variant="outlined"
              onClick={() => setVisibleCount(blogPosts.length)}
              sx={{
                borderColor: "primary.main",
                color: "primary.main",
                borderRadius: "20px 20px 0px 20px",
                px: 4,
                py: 1.2,
                fontWeight: 600,
                fontSize: "0.95rem",
                borderWidth: "2px",
                "&:hover": {
                  bgcolor: "primary.main",
                  color: "common.white",
                  borderWidth: "2px",
                },
              }}
            >
              Load More
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default Blogs;
